import api from './api';
import { API_ENDPOINTS } from './config';

/**
 * Crew Service
 * Handles all crew member CRUD operations including file uploads for photos
 */

/**
 * Fetch all crew members
 * @returns {Promise<Object>} { success, data: [{crew_member_id, name, role, etc.}], error }
 */
export const getAllCrew = async ({ page = 1, limit = 50 } = {}) => {
  try {
    const response = await api.post(API_ENDPOINTS.CREW.GET_ALL, {
      page,
      limit,
    });

    return response;
  } catch (error) {
    console.error("Get All Crew Error:", error);

    return {
      success: false,
      data: null,
      error: "Failed to fetch crew members",
    };
  }
};


/**
 * Fetch crew member by ID
 * @param {string|number} crewMemberId - The crew member ID
 * @returns {Promise<Object>} { success, data: {crew_member_id, name, role, etc.}, error }
 */
export const getCrewById = async (crewMemberId) => {
  try {
    const response = await api.get(`${API_ENDPOINTS.CREW.GET_BY_ID}/${crewMemberId}`);
    return response;
  } catch (error) {
    console.error('Get Crew By ID Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch crew member details',
    };
  }
};

/**
 * Create new crew member
 * @param {FormData} formData - FormData object containing crew member data and photo file
 * @returns {Promise<Object>} { success, data: {crew_member_id, ...}, error }
 */
export const createCrew = async (formData) => {
  try {
    const response = await api.post(API_ENDPOINTS.CREW.CREATE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    console.error('Create Crew Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to create crew member',
    };
  }
};

/**
 * Update existing crew member
 * @param {string|number} crewMemberId - The crew member ID
 * @param {FormData|Object} data - FormData object (if uploading file) or plain object
 * @returns {Promise<Object>} { success, data: {crew_member_id, ...}, error }
 */
export const updateCrew = async (crewMemberId, data) => {
  try {
    const isFormData = data instanceof FormData;
    const config = isFormData
      ? {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      : {};

    const response = await api.put(
      `${API_ENDPOINTS.CREW.UPDATE}/${crewMemberId}`,
      data,
      config
    );
    return response;
  } catch (error) {
    console.error('Update Crew Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to update crew member',
    };
  }
};

/**
 * Delete crew member
 * @param {string|number} crewMemberId - The crew member ID
 * @returns {Promise<Object>} { success, data: null, error }
 */
export const deleteCrew = async (crewMemberId) => {
  try {
    const response = await api.delete(`${API_ENDPOINTS.CREW.DELETE}/${crewMemberId}`);
    return response;
  } catch (error) {
    console.error('Delete Crew Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to delete crew member',
    };
  }
};


/**
 * Fetch all Crew count
 * @returns {Promise<Object>} { success, data: [equipment items], error }
 */
export const getCrewCount = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.CREW.GET_CREW_COUNT);
    return response;
  } catch (error) {
    console.error('Get All Crew Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch Crew',
    };
  }
};


/**
 * Step 1: Upload Crew CSV and get JSON preview
 * @param {FormData} formData - Contains the 'file' key with the CSV
 */
export const previewCrewCsv = async (formData) => {
  try {
    // This will always be FormData because we are uploading a file
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const response = await api.post(
      API_ENDPOINTS.CREW.BULK_PREVIEW || '/admin/bulk-upload-crew',
      formData,
      config
    );
    return response;
  } catch (error) {
    console.error('Preview Crew CSV Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to parse crew CSV file',
    };
  }
};

/**
 * Step 2: Save the mapped crew data to the database
 * @param {Object} data - Format: { crew_list: [...] }
 */
export const bulkSaveCrew = async (data) => {
  try {
    // This is standard JSON data
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const response = await api.post(
      API_ENDPOINTS.CREW.BULK_SAVE || '/admin/bulk-upload-save-crew',
      data,
      config
    );
    return response;
  } catch (error) {
    console.error('Bulk Save Crew Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to save crew members',
    };
  }
};


export const getCrewRoles = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.MASTER_DATA.ROLES || 'api/admin/crew-roles');
    return response.data; // Adjusted to return .data based on standard response shapes
  } catch (error) {
    console.error('Get Categories Error:', error);
    return {
      success: false,
      data: [],
      error: 'Failed to fetch categories',
    };
  }
};


export const getSkills = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.MASTER_DATA.SKILLS || 'api/admin/skills');
    return response.data; // Adjusted to return .data based on standard response shapes
  } catch (error) {
    console.error('Get Categories Error:', error);
    return {
      success: false,
      data: [],
      error: 'Failed to fetch categories',
    };
  }
};

export const publishCrew = async (crewMemberId) => {
  try {
    // This calls the specific publish logic that also handles the stats increment
    const response = await api.put(`api/admin/${crewMemberId}/publish`);
    return { success: true, data: response.data.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to publish crew member'
    };
  }
};
